<template>
  <div>
    <ais-refinement-list
      :attribute="facetName"
      :sort-by="sortBy"
      :limit="1000"
      operator="or"
      ref="refinementListRef"
    >
      <template v-slot="{ items, refine }" >
        <div v-show="items?.length === 0" class="text-xs bg-gray-75 text-indigo-base rounded-md mt-1">
          <p>No options available. Clear a filter on another category.</p>
        </div>
        <div v-show="items?.length !== 0" class="mt-2">
          <div v-for="item in items" :key="item.value">
            <label class="flex mt-4 items-center mt-0 mb-4">
              <input
                type="checkbox"
                :data-testid="`checkbox-${useKebabCase(item.value)}`"
                :value="item.value"
                :checked="
                  item.isRefined || selectedOptions.includes(item.value)
                "
                :class="[`${primaryColorClass}`, `${chkboxClass}`]"
                class="w-6 h-6 form-checkbox"
                @change="handleChecked($event.currentTarget.checked, item, refine)"
              />
              <div class="flex flex-1 ml-2">
                <p
                  class="text-xs font-montserrat text-indigo-base font-medium text-sm"
                  :data-testid="`checkbox-label-${useKebabCase(item.value)}`"
                >
                  {{ item.label }}
                </p>
                <div class="relative left-6">
                  <CircleBadge
                    v-if="showBadge && item.count > 0"
                    :width="23"
                    :height="22"
                    background-color="#E3E7F3"
                    text-class="text-[#436278] p-1 px-2"
                    css-class="rounded-[6px] !w-auto"
                    :value="item.count"
                  />
                </div>
              </div>
            </label>
          </div>
        </div>
      </template>
    </ais-refinement-list>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, toRefs, unref, watch } from "vue";
import { AisRefinementList } from "vue-instantsearch/vue3/es";
import CircleBadge from "@/components/shared/icons/CircleBadge.vue";
import { useKebabCase } from "@/composables/stringManupulation";

const checkedBoxes = ref([]);
const refinementListRef = ref(undefined);

const props = defineProps({
  filterName: String,
  algoliaConfiguration: {
    type: Object,
  },
  updateFilter: {
    type: Function, // Don't do this, for whatever reason the Algolia refine reverts if I use an emit
  },
  selectedOptions: {
    type: Array,
    default(rawProps) {
      return [];
    },
  },
  orderBy: {
    type: String,
    default: "name:asc",
  },
  checkboxClass: {
    type: String,
    default: "",
  },
  showBadge: {
    type: Boolean,
    default: false,
  },
});

const { algoliaConfiguration, filterName, orderBy } = toRefs(props);

const sortBy = ref([unref(orderBy)]);

const facetName = computed(() => {
  return unref(algoliaConfiguration)?.[unref(filterName)];
});

const chkboxClass = computed(() => props.checkboxClass);

const handleChecked = (newValue, item, refine) => {
  let updatedCheckedBoxes;
  if (newValue) {
    updatedCheckedBoxes = [...unref(checkedBoxes), `${item.value}`];
  } else {
    updatedCheckedBoxes = unref(checkedBoxes).filter((value) => value !== item.value);
  }
  checkedBoxes.value = updatedCheckedBoxes;
  refine(item.value);
  // emits("checkbox-filter", [...unref(checkedBoxes)]);
  // Don't do this, for whatever reason the Algolia refine reverts if I use an emit
  unref(props.updateFilter)(unref(props.filterName), [...updatedCheckedBoxes]);
};

watch(
  () => props.selectedOptions,
  (updatedOptions) => {
    const algoliaState = unref(refinementListRef)?.state;
    if (algoliaState?.canRefine && algoliaState?.items) {
      algoliaState?.items.forEach((item) => {
        const included = updatedOptions.includes(item.value);
        const refined = checkedBoxes.value.includes(item.value);
        if ((included && !refined) || (!included && refined)) {
          unref(refinementListRef).refine(item.value);
        }
      });
    }
    checkedBoxes.value = [...updatedOptions];
  },
  { immediate: true, deep: true },
);
</script>
